import { ActiveAssistant } from "@/components/ActiveAssistant";
import { ActivityTab } from "@/components/ActivityTab";
import { HomeBrief } from "@/components/workspace/HomeBrief";
import { ProgressBar } from "@/lib/phases";
import { isFullBusinessRole } from "@/lib/teamAccess";
import { ArrowRight, Camera, Plus, UserPlus, FileText, Stethoscope } from "@phosphor-icons/react";

export function HomeOverview({ business, phases, onGoToView, onScan, onAction }) {
  const totals = phases.reduce((acc, p) => {
    const pr = business.progress?.[p] || { done: 0, total: 0 };
    return { done: acc.done + pr.done, total: acc.total + pr.total };
  }, { done: 0, total: 0 });
  const setupDone = totals.total > 0 && totals.done >= totals.total;
  const full = isFullBusinessRole(business);

  const shortcuts = [
    { key: "scan", label: "Scan a receipt", Icon: Camera, onClick: onScan },
    { key: "txn", label: "Add a transaction", Icon: Plus, onClick: () => onAction("money") },
    ...(full ? [{ key: "customer", label: "Add a customer", Icon: UserPlus, onClick: () => onAction("customers") }] : []),
    { key: "invoice", label: "Send an invoice", Icon: FileText, onClick: () => onGoToView("invoices") },
    { key: "diagnose", label: "Diagnose my books", hint: "Also under Reports → Diagnose my books", Icon: Stethoscope, onClick: () => onGoToView("diagnose") },
  ];

  return (
    <div className="space-y-6" data-testid="home-overview">
      <HomeBrief businessId={business.id} onGoToView={onGoToView} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="home-shortcuts">
        {shortcuts.map((s) => (
          <button key={s.key} onClick={s.onClick} data-testid={`shortcut-${s.key}`} className="bk-card bk-lift p-4 text-left flex items-center gap-3">
            <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--bk-rowan-bg)" }}><s.Icon size={18} weight="duotone" color="var(--bk-accent)" /></span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{s.label}</span>
              {s.hint && <span className="block text-[11px] truncate" style={{ color: "var(--bk-text-2)" }}>{s.hint}</span>}
            </span>
          </button>
        ))}
      </div>

      <ActiveAssistant businessId={business.id} onGoToTab={onGoToView} />

      {phases.length > 0 && !setupDone && (
        <button onClick={() => onGoToView("setup_guides")} data-testid="setup-progress-line" className="bk-card bk-lift p-4 w-full text-left flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Setting up your books · {totals.done}/{totals.total} steps</p>
            <div className="mt-2"><ProgressBar done={totals.done} total={totals.total} /></div>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--bk-accent)" }}>Continue setup <ArrowRight size={14} weight="bold" /></span>
        </button>
      )}

      <ActivityTab businessId={business.id} canViewAll={full} />
    </div>
  );
}
